// AuthenticatedUser — the identity object MCP middleware constructs from a
// validated Identity Platform JWT. Non-obvious invariants enforced here:
//
//   1. platform_admin role REQUIRES studioId === null (cross-tenant scope).
//   2. tenant_admin / tenant_user REQUIRE studioId !== null (single-tenant).
//   3. contributor (S-722) REQUIRES studioId === null (same as
//      platform_admin — no tenant membership) AND contributorId !== null
//      (their own marketplace-contributor identity, the thing that scopes
//      them). Without invariant 3, a contributor would be indistinguishable
//      from platform_admin by studioId alone — contributorId is what keeps
//      the two null-studioId roles apart.
//   4. Every OTHER role requires contributorId === null — contributorId
//      only ever means something for a contributor.
//
// The Zod refine() encodes all four. Adapters never construct this
// directly — only middleware does, and only after JWT validation succeeds.
import { z } from "zod";
import { AccessTierSchema } from "../shared-kernel/AccessTier.js";
import { RoleSchema } from "../shared-kernel/Role.js";
import { CapabilitySchema } from "../shared-kernel/Capability.js";
export const AuthenticatedUserSchema = z
    .object({
    uid: z.string().min(1),
    role: RoleSchema,
    studioId: z.string().min(1).nullable(),
    // S-722 — a marketplace contributor's own identity. Parallel to
    // studioId (the tenant's identity): studioId scopes tenant_admin/
    // tenant_user to a tenant, contributorId scopes contributor to their
    // own listings. The two are mutually exclusive by construction (see
    // the superRefine below) — no principal is ever both. `.nullish()`,
    // NOT `.nullable()` like studioId — studioId's requiredness predates
    // this field and every one of the ~45 existing construction sites
    // across the repo already sets it explicitly, but forcing the same on
    // contributorId now would mean touching all of them just to add a
    // field only a brand-new role ever populates. Absent parses to
    // `undefined`, normalized to `null` in the superRefine below and by
    // every reader — same accommodation this file already makes for
    // `capabilities` below, for the identical reason.
    contributorId: z.string().min(1).nullable().optional(),
    accessTier: AccessTierSchema,
    jwtClaims: z.record(z.unknown()),
    // E-RBAC / S-313 — explicit capability grant resolved from the user's
    // Identity Platform custom claims (a tenant-defined custom role's
    // capability set). Absent/null means "use the built-in role's default
    // set" (ROLE_CAPABILITIES). The base `role` still governs tenant scope;
    // this only overrides the *action* set. Optional (`.nullish()`) so every
    // existing principal construction stays backward-compatible — a missing
    // claim parses to `undefined`, which the resolver treats as "use default".
    capabilities: z.array(CapabilitySchema).nullish(),
})
    .superRefine((user, ctx) => {
    // undefined (the ~45 pre-existing construction sites that predate this
    // field) and explicit null are the same thing: "not a contributor."
    const contributorId = user.contributorId ?? null;
    if (user.role === "platform_admin" && user.studioId !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["studioId"],
            message: "platform_admin role requires studioId === null",
        });
    }
    if ((user.role === "tenant_admin" || user.role === "tenant_user") &&
        user.studioId === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["studioId"],
            message: `${user.role} role requires a non-null studioId`,
        });
    }
    if (user.role === "contributor" && user.studioId !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["studioId"],
            message: "contributor role requires studioId === null",
        });
    }
    if (user.role === "contributor" && contributorId === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["contributorId"],
            message: "contributor role requires a non-null contributorId",
        });
    }
    if (user.role !== "contributor" && contributorId !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["contributorId"],
            message: `${user.role} role requires contributorId === null`,
        });
    }
});
//# sourceMappingURL=AuthenticatedUser.js.map